import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Reveal from "@/components/base/Reveal";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { getArticleById, getRelatedArticles } from "@/mocks/articles";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <section className="w-full max-w-3xl mx-auto px-4 md:px-8 py-24 text-center">
        <span className="mx-auto w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
          这条爆料不存在或已撤回
        </h1>
        <p className="mt-3 text-sm text-foreground-600">
          投稿人可以随时申请撤回线索，撤回后本页将不再展示。
        </p>
        <Link
          to="/square"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          返回爆料广场
        </Link>
      </section>
    );
  }

  const related = getRelatedArticles(article, 3);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      datePublished: article.date,
      dateModified: article.date,
      inLanguage: "zh-CN",
      articleSection: article.category,
      keywords: article.tags.join(","),
      author: { "@type": "Organization", name: "511爆料" },
      publisher: { "@type": "Organization", name: "511爆料" },
      mainEntityOfPage: `https://cqkczx.cn/article/${article.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "首页",
          item: "https://cqkczx.cn/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "爆料广场",
          item: "https://cqkczx.cn/square",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title,
          item: `https://cqkczx.cn/article/${article.id}`,
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${article.title} | 511爆料`}
        description={article.excerpt}
        canonical={`https://cqkczx.cn/article/${article.id}`}
        keywords={`${article.tags.join(",")},511爆料,匿名投稿`}
        type="article"
        jsonLd={jsonLd}
      />

      <article className="w-full max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
        <Breadcrumb
          items={[
            { label: "首页", path: "/" },
            { label: "爆料广场", path: "/square" },
            { label: article.category, path: `/square?category=${article.categoryId}` },
            { label: article.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/square?category=${article.categoryId}`}
                className="px-3 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors"
              >
                {article.category}
              </Link>
              <span className="px-3 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs">
                {article.status}
              </span>
              <span className="text-xs text-foreground-500">发布于 {article.date}</span>
            </div>

            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              {article.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-user-voice-line"></i>
                </span>
                {article.submitter}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-fire-line"></i>
                </span>
                {article.heat.toLocaleString()} 热度
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </span>
                {article.views.toLocaleString()} 阅读
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-chat-3-line"></i>
                </span>
                {article.comments} 讨论
              </span>
            </div>

            <div className="mt-6 w-full h-56 md:h-[380px] rounded-lg overflow-hidden bg-background-100">
              <img
                src={article.cover}
                alt={`${article.title} - 511爆料配图`}
                title={`${article.title} 511爆料`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-6">
              {article.content.map((section, index) => (
                <Reveal key={index} delay={index * 40}>
                  <div>
                    {section.heading && (
                      <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950">
                        {section.heading}
                      </h2>
                    )}
                    <p className="mt-2 text-sm md:text-base text-foreground-700 leading-loose">
                      {section.text}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-md bg-background-100 text-foreground-700 text-xs"
                >
                  # {tag}
                </span>
              ))}
            </div>

            <div className="mt-8 p-5 rounded-lg bg-secondary-50 border border-secondary-200/80">
              <p className="text-sm text-foreground-700 leading-relaxed">
                <strong className="text-foreground-900">投稿人须知：</strong>
                本内容由用户匿名投稿，平台已完成身份脱敏处理。若你掌握更完整的信息，可通过投稿入口补充线索；
                若你认为内容与事实不符，请通过投诉邮箱联系，我们会在核实后更新状态标签。
              </p>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/submit"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                <i className="ri-add-circle-line"></i>
                补充这条线索
              </Link>
              <Link
                to="/square"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors"
              >
                <i className="ri-list-check-2"></i>
                浏览更多爆料
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="p-5 rounded-lg bg-background-100 border border-background-200/80">
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  线索状态说明
                </h2>
                <ul className="mt-4 space-y-3 text-sm">
                  <li className="flex items-start gap-2 text-foreground-600">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500 shrink-0">
                      <i className="ri-fire-line"></i>
                    </span>
                    发酵中：已进入热门发酵环节，多方信源正在核实。
                  </li>
                  <li className="flex items-start gap-2 text-foreground-600">
                    <span className="w-5 h-5 flex items-center justify-center text-accent-600 shrink-0">
                      <i className="ri-checkbox-circle-line"></i>
                    </span>
                    已核实：核心事实已获得独立信源交叉确认。
                  </li>
                  <li className="flex items-start gap-2 text-foreground-600">
                    <span className="w-5 h-5 flex items-center justify-center text-secondary-600 shrink-0">
                      <i className="ri-error-warning-line"></i>
                    </span>
                    待补充：关键细节仍缺少佐证，欢迎继续投喂。
                  </li>
                </ul>
              </div>

              <div className="p-5 rounded-lg bg-primary-950 text-background-100">
                <h2 className="font-heading text-base font-semibold text-background-50">
                  有更猛的瓜？
                </h2>
                <p className="mt-2 text-sm text-background-200/90 leading-relaxed">
                  511爆料匿名投稿入口长期开放，支持私信投稿、投喂瓜料、我要爆料，线索直达后台审核。
                </p>
                <Link
                  to="/submit"
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-flashlight-line"></i>
                  立即投稿
                </Link>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-14 pt-10 border-t border-background-200/70">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            相关爆料推荐
          </h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {related.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        </section>
      </article>
    </>
  );
}